import { AxiosError } from 'axios';

const readOptionalResource = async <T>(request: Promise<{ data: T }>): Promise<T | null> => {
  try {
    return (await request).data;
  } catch (error) {
    if ((error as AxiosError).response?.status === 404) return null;
    throw error;
  }
};

const ensureDraftId = async ({
  eoaId,
  hasContent,
  createDraft,
}: {
  eoaId: string | null;
  hasContent: boolean;
  createDraft?: () => Promise<string | null>;
}): Promise<string | null> => {
  if (eoaId) return eoaId;
  if (!hasContent || !createDraft) return null;
  return createDraft();
};

interface PersistNestedConfigurationArgs<TForm, TPayload extends object> {
  form: TForm;
  previousForm: TForm;
  eoaId: string | null;
  exists: boolean;
  initialForm: TForm;
  hasContent: (form: TForm) => boolean;
  changedPayload: (previous: TForm, next: TForm) => TPayload;
  create: (eoaId: string, payload: TPayload) => Promise<unknown>;
  patch: (eoaId: string, payload: TPayload) => Promise<unknown>;
  createDraft?: () => Promise<string | null>;
}

const persistNestedConfiguration = async <TForm, TPayload extends object>({
  form,
  previousForm,
  eoaId,
  exists,
  initialForm,
  hasContent,
  changedPayload,
  create,
  patch,
  createDraft,
}: PersistNestedConfigurationArgs<TForm, TPayload>): Promise<boolean> => {
  const draftId = await ensureDraftId({ eoaId, hasContent: hasContent(form), createDraft });
  if (!draftId) return exists;

  if (!exists) {
    if (!hasContent(form)) return false;
    await create(draftId, changedPayload(initialForm, form));
    return true;
  }

  const payload = changedPayload(previousForm, form);
  if (Object.keys(payload).length === 0) return true;

  await patch(draftId, payload);
  return true;
};

export { ensureDraftId, persistNestedConfiguration, readOptionalResource };
export default persistNestedConfiguration;
